import { artifactDraftSchema, type ArtifactDraft } from "@base-hormonal/contracts";
import type { Database, Json } from "@base-hormonal/database";
import type { TypedSupabaseClient } from "../../../shared/api/supabase";
import { fromSupabaseError, PublicAppError } from "../../../shared/lib/errors";
import { withRequestSignal } from "../../../shared/lib/request";
import type { ProfessionalPreset, ProfileModuleId, UsageMode } from "../model/access";

export type AccountSettings = Database["public"]["Tables"]["account_settings"]["Row"];
export type AccountEntitlement = Database["public"]["Tables"]["account_entitlements"]["Row"];
export type Profile = Database["public"]["Tables"]["alunos"]["Row"];
export type ProfileModule = Database["public"]["Tables"]["perfil_modulos"]["Row"];
export type ArtifactTemplate = Database["public"]["Tables"]["artifact_templates"]["Row"];
export type TemplateArtifactType = "protocolo" | "treino" | "nutricao";

export interface ArtifactTemplateInput {
  nome: string;
  artifactType: TemplateArtifactType;
  payload: unknown;
}

export interface BriefingAlert {
  alerta_id: string;
  aluno_id: string;
  aluno_nome: string;
  tipo: "ciclo_proximo_fim" | "pouca_mudanca" | "exame_fora_referencia";
  severidade: "alta" | "media";
  titulo: string;
  detalhe: string;
  data_referencia: string | null;
  modulo: ProfileModuleId;
}

export interface AccountExperienceInput {
  usageMode: UsageMode;
  professionalProfile: ProfessionalPreset | null;
  defaultModules: ProfileModuleId[];
  completeOnboarding: boolean;
}

export interface ProfileInput {
  id?: string;
  nome: string;
  objetivo: string | null;
  observacoes: string | null;
  dataNascimento: string | null;
  sexoBiologico: string | null;
  alturaCm: number | null;
  ativo: boolean;
}

function raise(error: { code?: string } | null): void {
  const safe = fromSupabaseError(error);
  if (safe !== null) throw safe;
}

export function createPlatformApi(client: TypedSupabaseClient) {
  return {
    async artifactTemplates(userId: string, signal?: AbortSignal): Promise<ArtifactTemplate[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("artifact_templates")
          .select("id,user_id,nome,artifact_type,schema_version,payload_json,archived_at,created_at,updated_at")
          .eq("user_id", userId)
          .is("archived_at", null)
          .order("updated_at", { ascending: false })
          .abortSignal(activeSignal);
        raise(error);
        return data ?? [];
      });
    },

    async saveArtifactTemplate(
      userId: string,
      input: ArtifactTemplateInput,
    ): Promise<ArtifactTemplate> {
      return withRequestSignal(undefined, async (signal) => {
        const { data, error } = await client
          .from("artifact_templates")
          .insert({
            user_id: userId,
            nome: input.nome,
            artifact_type: input.artifactType,
            schema_version: 1,
            payload_json: input.payload as Json,
          })
          .select("id,user_id,nome,artifact_type,schema_version,payload_json,archived_at,created_at,updated_at")
          .abortSignal(signal)
          .single();
        raise(error);
        if (data === null) throw new PublicAppError("empty_response", "O template não foi salvo.");
        return data;
      });
    },

    async deleteArtifactTemplate(templateId: string): Promise<void> {
      const { error } = await client
        .from("artifact_templates")
        .update({ archived_at: new Date().toISOString() })
        .eq("id", templateId);
      raise(error);
    },

    async applyArtifactTemplate(templateId: string, profileId: string): Promise<ArtifactDraft> {
      const { data, error } = await client.rpc("aplicar_template_artefato", {
        p_template_id: templateId,
        p_aluno_id: profileId,
      });
      raise(error);
      return artifactDraftSchema.parse(data);
    },

    async dailyBriefing(signal?: AbortSignal): Promise<BriefingAlert[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .rpc("obter_briefing_diario", {})
          .abortSignal(activeSignal);
        raise(error);
        return (data ?? []) as BriefingAlert[];
      });
    },

    async accountSettings(userId: string, signal?: AbortSignal): Promise<AccountSettings | null> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("account_settings")
          .select(
            "user_id,usage_mode,professional_profile,default_profile_modules,locale,timezone,onboarding_completed_at,created_at,updated_at",
          )
          .eq("user_id", userId)
          .abortSignal(activeSignal)
          .maybeSingle();
        raise(error);
        return data;
      });
    },

    async entitlement(userId: string, signal?: AbortSignal): Promise<AccountEntitlement | null> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("account_entitlements")
          .select("user_id,capabilities,limits,source,updated_at")
          .eq("user_id", userId)
          .abortSignal(activeSignal)
          .maybeSingle();
        raise(error);
        return data;
      });
    },

    async profiles(userId: string, signal?: AbortSignal): Promise<Profile[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("alunos")
          .select(
            "id,user_id,nome,objetivo,observacoes,tipo_perfil,data_nascimento,sexo_biologico,altura_cm,ativo,created_at,updated_at",
          )
          .eq("user_id", userId)
          .order("nome")
          .abortSignal(activeSignal);
        raise(error);
        return data ?? [];
      });
    },

    async modules(profileIds: string[], signal?: AbortSignal): Promise<ProfileModule[]> {
      if (profileIds.length === 0) return [];
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("perfil_modulos")
          .select("aluno_id,modulo,habilitado,configuracao,created_at,updated_at")
          .in("aluno_id", profileIds)
          .abortSignal(activeSignal);
        raise(error);
        return data ?? [];
      });
    },

    async saveExperience(userId: string, input: AccountExperienceInput): Promise<AccountSettings> {
      const modules = [...new Set<ProfileModuleId>(["assistente", ...input.defaultModules])];
      return withRequestSignal(undefined, async (signal) => {
        const { data, error } = await client
          .from("account_settings")
          .upsert(
            {
              user_id: userId,
              usage_mode: input.usageMode,
              professional_profile:
                input.usageMode === "professional" ? (input.professionalProfile ?? "other") : null,
              default_profile_modules: modules,
              onboarding_completed_at: input.completeOnboarding ? new Date().toISOString() : null,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id" },
          )
          .select(
            "user_id,usage_mode,professional_profile,default_profile_modules,locale,timezone,onboarding_completed_at,created_at,updated_at",
          )
          .abortSignal(signal)
          .single();
        raise(error);
        if (data === null)
          throw new PublicAppError("empty_response", "A configuração não foi salva.");
        return data;
      });
    },

    async ensurePersonalProfile(userId: string, displayName: string): Promise<Profile> {
      const existing = await this.profiles(userId).then((rows) =>
        rows.find((profile) => profile.tipo_perfil === "pessoal"),
      );
      if (existing !== undefined) return existing;
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("alunos")
          .insert({ user_id: userId, nome: displayName.slice(0, 160), tipo_perfil: "pessoal" })
          .abortSignal(signal);
        if (error !== null && error.code !== "23505") raise(error);
      });
      const created = await this.profiles(userId).then((rows) =>
        rows.find((profile) => profile.tipo_perfil === "pessoal"),
      );
      if (created === undefined) {
        throw new PublicAppError("profile_unavailable", "Meu perfil não ficou disponível.");
      }
      return created;
    },

    async saveProfile(userId: string, input: ProfileInput): Promise<void> {
      const values = {
        nome: input.nome,
        objetivo: input.objetivo,
        observacoes: input.observacoes,
        data_nascimento: input.dataNascimento,
        sexo_biologico: input.sexoBiologico,
        altura_cm: input.alturaCm,
        ativo: input.ativo,
        updated_at: new Date().toISOString(),
      };
      await withRequestSignal(undefined, async (signal) => {
        const result = input.id
          ? await client.from("alunos").update(values).eq("id", input.id).abortSignal(signal)
          : await client
              .from("alunos")
              .insert({ ...values, user_id: userId, tipo_perfil: "cliente" })
              .abortSignal(signal);
        raise(result.error);
      });
    },

    async deleteClientProfile(profileId: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("alunos")
          .delete()
          .eq("id", profileId)
          .eq("tipo_perfil", "cliente")
          .abortSignal(signal);
        raise(error);
      });
    },

    async saveModules(profileId: string, enabled: ProfileModuleId[]): Promise<void> {
      const selected = new Set<ProfileModuleId>(["assistente", ...enabled]);
      const rows = (
        ["assistente", "protocolos", "exames", "nutricao", "treino", "corpo", "evolucao"] as const
      ).map((module) => ({
        aluno_id: profileId,
        modulo: module,
        habilitado: selected.has(module),
        updated_at: new Date().toISOString(),
      }));
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("perfil_modulos")
          .upsert(rows, { onConflict: "aluno_id,modulo" })
          .abortSignal(signal);
        raise(error);
      });
    },
  };
}

export type PlatformApi = ReturnType<typeof createPlatformApi>;
